import { Button, Dialog, DialogFooter, Icon, Link, Loading, setNotification } from '@startlibs/components'
import {Errors, TextInput, WithForm, useErrors} from '@startlibs/form'
import {useNavigate} from '@startlibs/components';;
import {useToggle} from '@startlibs/core'
import React, { useRef, useState } from 'react';
import _ from 'lodash/fp'
import styled from 'styled-components';
import {FormattedMessage, useIntl} from 'react-intl';
import {CASE_CLOSED, CASE_REVIEWED} from '../../enums/CaseState'
import {Card, HeaderButtons, PageContainer, PageFooter} from '../../components/PageLayout';
import {CustomReportInfoBox} from '../../expert/customReport/CustomReportInfoBox'
import {ExternalReportUpload} from '../../request/components/ExternalReportUpload'
import {FinalReportEdit} from './finalReport/FinalReportEdit'
import {Header} from '../../components/Header';
import { PageLoadingSuspense } from '../../components/PageLoading';
import {PdfReportFrame, PdfReportFrameWrapper} from '../../request/components/PdfReportFrame';
import {Preview} from '../../request/reviewedRequest/Preview'
import {PreviewForCaseRequest} from '../../request/reviewedRequest/PreviewForCaseRequest'
import {PurviewFooter} from '../../components/PurviewFooter';
import {SuccessBox} from '../../components/InfoBox';
import {getJwt} from '../../hooks/useJwt'
import {hasAnsweredAll} from '../../request/utils'
import {lazyProviderInfo, lazyProviderInfoSec, lazyUserInfo} from '../../components/WithProvider'
import {useConfirmAnswers} from '../../expert/customReport/useConfirmAnswers'
import { useConfirmDialog, ConfirmDialog } from '../../hooks/useConfirmDialog'
import {useConfirmFinishCase} from './hooks/useConfirmFinishCase'
import {useIframeIsLoading} from './hooks/useIframeIsLoading'
import {useNotifyClosedCase} from './hooks/useNotifyClosedCase'
import {useShareDialog} from '../../hooks/useShareDialog'
import {useUploadCustomReport, useUploadDialogIsOpen} from '../../expert/customReport/useUploadCustomReport'
import { buildValidation, required } from '../../utils/validation';
import { IfLocale } from '../../hocs/IfLocale';
import { jwtPostFetcher } from '../../utils/authFetch';
import { AssignmentStatusBox } from './expertReview/ExpertReviewList';
import { AdminExpertTime } from '../../request/expertTime/AdminExpertTime';
import { ExpertTimeHeading, getExpertTimeErrorMessage } from '../../request/expertTime/ExpertTime';
import { EXPERT_TIME_HIDDEN, EXPERT_TIME_REQUIRED, getExpertTimeMode, useExpertTimeEntries } from '../../request/expertTime/expertTimeUtils';
import { SUPER } from '../../enums/UserRole';

const ActionsWrapper = styled.div`
  display: flex;
  ${Button} {
    margin-left: 1rem;
  }
`

export const PrintPdfPreview = ({caseRequest}) => {
  const providerInfo = lazyProviderInfo.read()

  return <Preview
    caseRequest={caseRequest}
    organization={{...providerInfo, ...providerInfo.address}}
  />
}

export const FinalReportStep = ({caseRequest, setCaseRequest, uploadPDF}) => {

  const navigate = useNavigate()
  const [, shareDialog] = useShareDialog(caseRequest)
  const sendToEHRDialog = useToggle()

  const providerInfo = lazyProviderInfo.read()
  const providerInfoSec = lazyProviderInfoSec.read()
  const isSignatureRequired = providerInfoSec?.reportSignatureRequired || false

  const expertTime = useExpertTimeEntries(caseRequest.requestId)
  const expertTimeMode = getExpertTimeMode(providerInfoSec)
  const isExpertTimeRequired = expertTimeMode === EXPERT_TIME_REQUIRED
  const customReportServing = !!caseRequest.report?.useCustomReport
  const canEditExpertTime = (caseRequest.state === CASE_REVIEWED && customReportServing) ||
    (caseRequest.state === CASE_CLOSED && lazyUserInfo.read()?.role === SUPER)
  // With the generated report the times are edited on the expert review page.
  const editExpertTimeOnReview = caseRequest.state === CASE_REVIEWED && !customReportServing
  const showExpertTime = expertTimeMode !== EXPERT_TIME_HIDDEN &&
    (canEditExpertTime || editExpertTimeOnReview ? expertTime.entries.length > 0 : expertTime.entries.some(({minutes}) => minutes !== null && minutes !== undefined))
  const expertTimeMissing = isExpertTimeRequired && caseRequest.state === CASE_REVIEWED && customReportServing &&
    expertTime.entries.length > 0 && expertTime.entries.every(({minutes}) => minutes === null || minutes === undefined)
  const [expertTimeBlocked, setExpertTimeBlocked] = useState(false)
  const [expertTimeErrors, expertTimeErrorsElement] = useErrors()
  const expertTimeRef = useRef()
  const intl = useIntl()

  React.useEffect(() => {
    if (!expertTimeMissing) {
      setExpertTimeBlocked(false)
      expertTimeErrors.clearErrors()
    }
  }, [expertTimeMissing])

  const finish = useConfirmFinishCase(caseRequest, setCaseRequest)
  const notify = useNotifyClosedCase(caseRequest)

  const uploadCustomReport = useUploadCustomReport(caseRequest, setCaseRequest)

  const iframeIsLoading = useIframeIsLoading(caseRequest.report?.useCustomReport)

  const confirmAnswers = useConfirmAnswers(caseRequest)

  const confirmMissingSignatureDialog = useConfirmDialog(
    <ConfirmDialog
      title="Signature required"
      onSuccess={() => navigate(`/admin/request/${caseRequest.requestCode}/review#signature`)}
      confirm={<Button highlight>Edit expert review</Button>}
      closeLabel="Cancel"
    >
      <p>You must sign the review before proceeding.</p>
    </ConfirmDialog>
  )

  const editing = useToggle(caseRequest.state === CASE_REVIEWED)

  const [isUploadDialogOpen] = useUploadDialogIsOpen()

  const caseCompletedWithoutReport = caseRequest.state === CASE_CLOSED && !caseRequest.report?.customReport && !caseRequest.report?.useCustomReport && !caseRequest.report
  const showUpload = (uploadPDF || (!hasAnsweredAll(caseRequest.caseInfo.questionsConsultant) && !caseRequest.report?.customReport)) && !caseRequest.report?.useCustomReport

  const tryToFinish = () => {
    if (showExpertTime && expertTimeMissing) {
      setExpertTimeBlocked(true)
      expertTimeErrors.setErrors({expertTime: getExpertTimeErrorMessage(intl, null, true)})
      expertTimeRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
      return
    }
    if (isSignatureRequired && !caseRequest.report?.useCustomReport && (!caseRequest.caseInfo.signatures || caseRequest.caseInfo.signatures.length === 0)) {
      confirmMissingSignatureDialog()
      return
    }
    if (!caseRequest.report?.useCustomReport) {
      confirmAnswers().then(finish)
    } else {
      finish()
    }
  }

  React.useEffect(() => {
    if (caseRequest.state === CASE_CLOSED)
    editing.close()
  },[caseRequest.state])


  const reviewedNavigation = caseRequest.state === CASE_REVIEWED &&
    <>
      <Button onClick={() => navigate(`/admin/request/${caseRequest.requestCode}/review`)}>Edit expert review</Button>
      {
        (!caseRequest.report?.useCustomReport && !uploadPDF && hasAnsweredAll(caseRequest.caseInfo.questionsConsultant) != 0) &&
        <Button icon={editing.isOpen ? "view" : "edit"} onClick={editing.toggle}>{editing.isOpen ? "Preview" : "Edit report"}</Button>
      }
      <Button highlight onClick={tryToFinish} disabled={showUpload && !caseRequest.report?.customReport}>Complete report</Button>
    </>


  return <><PageContainer withPrint={true}>
    <Header title="Case report" noPrint>
      {reviewedNavigation}
      {
        caseRequest.state === CASE_CLOSED &&
        <ActionsWrapper>
          <SuccessBox>
            <Icon icon="check"/><span>{caseCompletedWithoutReport ? 'Case' : 'Report'} completed.</span>
          </SuccessBox>
          <Button icon="mail" onClick={notify}>Notify contact</Button>
        </ActionsWrapper>
      }
    </Header>
    {caseRequest.state === CASE_CLOSED &&
    <HeaderButtons noPrint>
      <div className="left-wrapper">
        <Button onClick={() => navigate(`/admin/request/${caseRequest.requestCode}/overview`)}>Go to overview</Button>
      </div>
      {!caseCompletedWithoutReport &&
        <div className="right-wrapper">
          <IfLocale not contains="LEGAL">
            {providerInfo?.ehrHl7Enabled === true && providerInfo?.caseReconciliationEnabled === true && <Button.a
              onClick={() => sendToEHRDialog.open()}
              // href={`/api/report/download?token=${getJwt()}&requestId=${caseRequest.requestId}`}
              icon="external-report"
            >Send to EHR</Button.a>}
          </IfLocale>
          <Button.a
            href={`/api/report/download?token=${getJwt()}&requestId=${caseRequest.requestId}`}
            icon="download"
          >Save PDF</Button.a>
          <Button.a
            target="_blank"
            href={`/api/report/preview?token=${getJwt()}&requestId=${caseRequest.requestId}`}
            icon="print"
          >Print</Button.a>
          {/* <Button highlight onClick={openShareDialog} icon="send">Share</Button> */}
        </div>
      }
    </HeaderButtons>
    }
    {
      caseRequest.report?.customReport && caseRequest.state === CASE_REVIEWED &&
      <CustomReportInfoBox setCaseRequest={setCaseRequest} caseRequest={caseRequest}/>
    }
    {
      !caseRequest.report?.customReport && caseRequest.state === CASE_REVIEWED && !showUpload &&
      <HeaderButtons>
        <div className="left-wrapper">
          <p css="margin-bottom:  0;">{editing.isOpen ? "Editing the default case report." : "Previewing default case report."}</p>
        </div>
        <div className="right-wrapper">
          <Button onClick={uploadCustomReport} small icon="upload">Upload custom PDF report</Button>
        </div>
      </HeaderButtons>
    }
    <PageLoadingSuspense>
    {
      caseCompletedWithoutReport 
        ? <AssignmentStatusBox reviewed={caseRequest.state === CASE_CLOSED}>
            <div className="waitingBoxContent">
              <Icon icon={'check'}/>
              <h4>
                {'This case is already completed.'}
              </h4>
              <p>The case has been marked as completed, but no report was generated.</p>
            </div>
          </AssignmentStatusBox> 
        : (showUpload)
            ? <UploadCustomReport caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>
            : caseRequest.report?.useCustomReport
              ? <PdfReportFrameWrapper>
                  <PdfReportFrame
                    pdfReport
                    isUploadDialogOpen={isUploadDialogOpen}
                    onLoad={iframeIsLoading.close}
                    src={`/api/report/preview?token=${getJwt()}&requestId=${caseRequest.requestId}&refreshPdf=${caseRequest.pdfKey}#toolbar=0&navpanes=0`}
                  />
                    {iframeIsLoading.isOpen && <Loading absolute/>}
                </PdfReportFrameWrapper>
              : (
                editing.isOpen
                  ? <FinalReportEdit
                      caseRequest={caseRequest}
                      editing={editing}
                      setCaseRequest={setCaseRequest}
                      providerInfo={providerInfo}
                    />
                  : <PreviewForCaseRequest caseRequest={caseRequest}/>
                  /*
                  <Load value={lazyPoviderInfo}>{ info =>
                    <PreviewForReport info={info} report={caseRequest.report}/>
                  }</Load>
                  */
              )
    }

    {showExpertTime && <div ref={expertTimeRef} css="@media print { display: none; }">
      <ExpertTimeHeading css="margin-top: 2rem;" required={expertTimeMissing || (isExpertTimeRequired && canEditExpertTime)} hasError={expertTimeBlocked && expertTimeMissing}>
        {editExpertTimeOnReview &&
          <Button small icon="edit" onClick={() => navigate(`/admin/request/${caseRequest.requestCode}/review#time`)}>
            <FormattedMessage defaultMessage="Edit" description="Expert time spent edit button"/>
          </Button>
        }
      </ExpertTimeHeading>
      <AdminExpertTime
        readOnly={!canEditExpertTime}
        showEmpty={editExpertTimeOnReview}
        hasError={expertTimeBlocked && expertTimeMissing}
        requestId={caseRequest.requestId}
        entries={expertTime.entries}
        required={isExpertTimeRequired}
        onEntryChange={expertTime.updateEntry}
      />
      <div css="margin: -1rem 0 2rem;">{expertTimeErrorsElement}</div>
    </div>}

    {reviewedNavigation && <PageFooter>{reviewedNavigation}</PageFooter>}

    {caseRequest.state === CASE_CLOSED &&
    <HeaderButtons noPrint>
      <div className="left-wrapper">
        <Button onClick={() => navigate(`/admin/request/${caseRequest.requestCode}/overview`)}>Go to overview</Button>
      </div>
      {!caseCompletedWithoutReport && <div className="right-wrapper">
        <Button.a
          href={`/api/report/download?token=${getJwt()}&requestId=${caseRequest.requestId}`}
          icon="download"
        >Save PDF</Button.a>
        <Button.a
          target="_blank"
          href={`/api/report/preview?token=${getJwt()}&requestId=${caseRequest.requestId}`}
          icon="print"
        >Print</Button.a>
        {/* <Button highlight onClick={openShareDialog} icon="send">Share</Button> */}
      </div>}
    </HeaderButtons>
    }
    </PageLoadingSuspense>
  </PageContainer>
    {shareDialog}
    {sendToEHRDialog.isOpen && <SendToEHRDialog closeDialog={sendToEHRDialog.close} caseRequest={caseRequest} setCaseRequest={setCaseRequest} />}
    <PurviewFooter/>
  </>
}


const UploadCustomReport = ({caseRequest, setCaseRequest}) => {
  const isUploading = useToggle()

  return <>
    <p><strong>There are missing fields in the Expert Review step.</strong> Would you like to upload a custom PDF report with the review?</p>
    <Card>
      {isUploading.isOpen && "Uploading..."}
      <WithForm>
        <ExternalReportUpload
          url={"/api/customReport?requestId=" + caseRequest.requestId}
          onSuccess={() => setCaseRequest(_.flow(_.set('report.useCustomReport', true), _.set('pdfKey', Date.now()), _.set('report.customReport', true)))}
        />
        <Errors/>
      </WithForm>
    </Card>
    <p>You can also <Link
      className="link"
      to={`/admin/request/${caseRequest.requestCode}/review`}
    >insert your answers</Link> for generating a pre-formatted Expert View report.</p>
  </>
}

const SendToEHRDialog = ({closeDialog, caseRequest, setCaseRequest }) => {

  const [, setLoading] = useState(false)

  const preValidation = buildValidation({
    'caseInfo.ehrInfo.mrn': [
      (v) => (!v?.length || v === null) && required(),
      (v) => v?.length > 255 && 'Medical Record Number must be 255 characters or less.',
    ],
    'caseInfo.ehrInfo.orderNumber': (v) => v?.length > 255 && 'Order Number must be 255 characters or less.',
  })

  const action = ({caseInfo}) => jwtPostFetcher(getJwt())("/api/caseInfo?requestId=" + caseRequest.requestId, caseInfo, {method: "PUT"})

  return <Dialog
    title="Send to EHR"
    closeDialog={closeDialog}
    isLoading={false}
  >
    <p css="font-size:14px !important;margin-bottom:1.5rem !important;">
      Insert the information below to export this case to your EHR system
    </p>
    <WithForm
      alwaysSave
      values={caseRequest}
      preValidation={preValidation}
      action={action}
      onSuccess={(values) => {
        setCaseRequest(values)
        setLoading(true)
        return jwtPostFetcher(getJwt())("/api/admin/sendToEhr?requestId=" + caseRequest.requestId, {}, {method: "POST"})
          .then((_response) => {
            setLoading(false)
            setNotification("Case report was sent to EHR")
            closeDialog()
          })
          .catch((_err) => {
            setLoading(false)
            setNotification({type: "alert", msg: (_close) => <>Error sending to EHR</>})
          })
        }
      }
    >{form => 
      <>
        
          <TextInput
            label="Medical Record Number"
            helpText="(Patient Identification Number)"
            path="caseInfo.ehrInfo.mrn"
            mandatory
            maxLength={255}
          />
          <TextInput
            label="Order Number"
            path="caseInfo.ehrInfo.orderNumber"
            maxLength={255}
          />
        
        <p>If the order number is empty, the case report will be seen as unsolicited message on the EHR system.</p>  
        <Errors/>
        <DialogFooter>
          <Button type='button' 
            onClick={closeDialog}
          >Cancel</Button>
          <Button 
            type='submit' 
            // disabled={form.getValue('caseInfo.ehrInfo.mrn') == null || form.getValue('caseInfo.ehrInfo.mrn').length === 0} 
            highlight 
            isLoading={form.isLoading} 
            // onClick={() => form.clearErrors()}
          >Send</Button>
        </DialogFooter>
      </>
    }</WithForm>
  </Dialog>
}